import { z } from 'zod'

export const emailSchema = z
  .string()
  .min(1, 'Email is required')
  .email('Please enter a valid email address (e.g., user@example.com)')

export const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters long')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/\d/, 'Password must contain at least one number')
  .regex(
    /[!@#$%^&*()_+={}|<>?~-]/,
    'Password must contain at least one special character (!@#$%^&*()_+={}|<>?~-)',
  )

export const updatePasswordSchema = z.object({
  new_password1: passwordSchema,
  new_password2: passwordSchema,
})

export const loginSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
})

export const passwordResetSchema = z.object({
  email: emailSchema,
})

export const reportUsernameSchema = z
  .object({
    deal: z.string().optional(),
    room_username: z
      .string()
      .min(4, 'Username field is required and must be at least 4 characters long')
      .max(20, 'Username must be between 4 and 20 characters'),
    room_email: emailSchema
      .min(5, 'Email must be at least 5 characters long')
      .max(45, 'Email must be must be between 5 and 45 characters'),
    creation_type: z.enum(['NEW', 'OLD']).optional(),
    poker_site: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.creation_type === 'NEW' && (!(data.deal ?? '') || data.deal?.trim() === '')) {
      ctx.addIssue({
        code: 'custom',
        message: 'Deal is required for NEW creation type.',
        path: ['deal'],
      })
    }
    if (
      data.creation_type === 'OLD' &&
      (!(data.poker_site ?? '') || data.poker_site?.trim() === '')
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'Poker Site is required for OLD creation type.',
        path: ['poker_site'],
      })
    }
  })

export const signupSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  _confirmPassword: passwordSchema,
  _terms: z.boolean().refine((val) => {
    return val
  }),
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  allow_marketing_emails: z.boolean().optional(),
})

export const settingsSchema = z.object({
  whatsapp_number: z
    .string()
    .refine(
      (val) => val === '' || /^\+[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/.test(val),
      'Please enter a valid WhatsApp number with country code (e.g., +123456789)',
    )

    .nullable()
    .optional(),
  discord_tag: z.string().nullable().optional(),
  telegram_contact: z
    .string()

    .refine(
      (val) => val === '' || /^\+[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/.test(val),
      'Please enter a valid Telegram contact (e.g., +123456789 or @username)',
    )
    .or(
      z
        .string()
        .regex(
          /^@([A-Za-z0-9_]{5,32})$/,
          'Please enter a valid Telegram username (e.g., @username)',
        ),
    )
    .nullable()
    .optional(),
  allow_marketing_emails: z.boolean().optional(),
})

export const passwordChangeSchema = z.object({
  current_password: passwordSchema,
  new_password: passwordSchema,
  new_password2: passwordSchema,
})

export const redeemSubscriptionSchema = z.object({
  gto_wizard_email: emailSchema,
})

export type ValidationError = {
  field: keyof LoginFormData
  message: string
}

export type LoginFormData = z.infer<typeof loginSchema>
export type SignupFormData = z.infer<typeof signupSchema>
export type PasswordResetFormData = z.infer<typeof passwordResetSchema>
export type UpdatePasswordFormData = z.infer<typeof updatePasswordSchema>
export type ReportUsernameFormData = z.infer<typeof reportUsernameSchema>
export type SettingsFormData = z.infer<typeof settingsSchema>
export type PasswordChangeFormData = z.infer<typeof passwordChangeSchema>
export type RedeemSubscriptionFormData = z.infer<typeof redeemSubscriptionSchema>

export type PassowrdChangeFormField = keyof PasswordChangeFormData
export type SettingsFormField = keyof SettingsFormData
export type UpdatePasswordFormField = keyof UpdatePasswordFormData
export type LoginFormField = keyof LoginFormData
export type SignupFormField = keyof SignupFormData
export type PasswordResetFormField = keyof PasswordResetFormData
export type ReportUsernameFormField = keyof ReportUsernameFormData
export type RedeemSubscriptionFormField = keyof RedeemSubscriptionFormData
